import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../utils/api';

const short = (addr) => addr ? `${addr.slice(0,5)}…${addr.slice(-4)}` : '';
const fmtDate = (d) => d ? new Date(d).toLocaleString() : 'Never';

export const WalletsPage = ({ toast }) => {
  const [wallets,  setWallets]  = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [submitting, setSub]    = useState(false);
  const [address,  setAddress]  = useState('');
  const [label,    setLabel]    = useState('');
  const [selected, setSelected] = useState(null);
  const [activity, setActivity] = useState([]);
  const [actLoad,  setActLoad]  = useState(false);

  const loadWallets = useCallback(async () => {
    try {
      const r = await api.getWallets();
      setWallets(r.data || []);
    } catch (e) { toast(e.message, 'error'); }
    finally { setLoading(false); }
  }, [toast]);

  useEffect(() => { loadWallets(); }, [loadWallets]);

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!address.trim()) return;
    setSub(true);
    try {
      await api.addWallet({ address: address.trim(), label: label.trim() });
      toast('Wallet added and listener started!');
      setAddress(''); setLabel('');
      loadWallets();
    } catch (e) { toast(e.message, 'error'); }
    finally { setSub(false); }
  };

  const handleRemove = async (addr) => {
    if (!window.confirm(`Stop tracking ${short(addr)}?`)) return;
    try {
      await api.removeWallet(addr);
      toast('Wallet removed');
      if (selected === addr) setSelected(null);
      loadWallets();
    } catch (e) { toast(e.message, 'error'); }
  };

  const loadActivity = async (addr) => {
    setSelected(addr); setActLoad(true);
    try {
      const r = await api.getWalletActivity(addr, { limit: 15 });
      setActivity(r.data || []);
    } catch (e) { toast(e.message, 'error'); }
    finally { setActLoad(false); }
  };

  return (
    <div style={S.page}>
      {/* Add wallet form */}
      <div style={S.card}>
        <h2 style={S.cardTitle}>Track New Wallet</h2>
        <form onSubmit={handleAdd} style={S.form}>
          <div style={S.field}>
            <label>Solana Address</label>
            <input value={address} onChange={e => setAddress(e.target.value)}
              placeholder="Enter Solana wallet address (base58)…" required />
          </div>
          <div style={S.field}>
            <label>Label (optional)</label>
            <input value={label} onChange={e => setLabel(e.target.value)}
              placeholder="e.g. Whale Trader #1" maxLength={50} />
          </div>
          <button type="submit" style={S.btn} disabled={submitting}>
            {submitting ? '⏳ Adding…' : '+ Add Wallet'}
          </button>
        </form>
      </div>

      {/* Wallet list */}
      <div style={S.card}>
        <h2 style={S.cardTitle}>Tracked Wallets
          <span style={S.badge}>{wallets.length}</span>
        </h2>

        {loading ? <p style={S.dim}>Loading…</p> :
         wallets.length === 0 ? <p style={S.dim}>No wallets tracked yet. Add one above.</p> :
         wallets.map(w => (
          <div key={w._id} style={{ ...S.walletRow, borderColor: selected === w.address ? '#00ff8840' : '#1e2d3d' }}>
            <div style={S.walletMain}>
              <div style={S.walletAddr}>
                <span style={S.dot(w.isActive)} />
                <code style={S.mono}>{short(w.address)}</code>
                {w.label && <span style={S.lbl}>{w.label}</span>}
              </div>
              <div style={S.walletMeta}>
                {w.totalTransactions} tx · Last: {fmtDate(w.lastActivityAt)}
              </div>
            </div>
            <div style={S.walletActions}>
              <button style={S.btnSm} onClick={() => loadActivity(w.address)}>Activity</button>
              <button style={{ ...S.btnSm, ...S.btnDanger }} onClick={() => handleRemove(w.address)}>Remove</button>
            </div>
          </div>
        ))}
      </div>

      {/* Activity drawer */}
      {selected && (
        <div style={S.card}>
          <h2 style={S.cardTitle}>Activity — <code style={S.mono}>{short(selected)}</code></h2>
          {actLoad ? <p style={S.dim}>Loading activity…</p> :
           activity.length === 0 ? <p style={S.dim}>No activity recorded yet.</p> :
           activity.map(a => <ActivityRow key={a._id} activity={a} />)}
        </div>
      )}
    </div>
  );
};

const ActivityRow = ({ activity: a }) => {
  const color = a.eventType === 'swap' ? '#00ff88' : a.eventType === 'sol_transfer' ? '#4d9eff' : '#ffd166';
  return (
    <div style={S.actRow}>
      <span style={{ ...S.tag, color, borderColor: color + '40', background: color + '10' }}>
        {a.eventType}
      </span>
      <div style={S.actDetail}>
        {a.eventType === 'swap' && a.tokenBought &&
          <span>Bought <code style={S.mono}>{short(a.tokenBought)}</code> · {a.amountBought?.toFixed(4)}</span>}
        {a.eventType === 'sol_transfer' && <span>SOL transfer</span>}
        {a.eventType === 'token_transfer' && <span>Token transfer</span>}
      </div>
      <a href={`https://solscan.io/tx/${a.signature}`} target="_blank" rel="noreferrer" style={S.txLink}>
        {short(a.signature)} ↗
      </a>
      <span style={S.time}>{new Date(a.createdAt).toLocaleTimeString()}</span>
    </div>
  );
};

const S = {
  page:    { display:'flex', flexDirection:'column', gap:24 },
  card:    { background:'#0d1117', border:'1px solid #1e2d3d', borderRadius:12, padding:'24px 28px' },
  cardTitle:{ fontFamily:"'Syne',sans-serif", fontWeight:800, fontSize:18, marginBottom:20,
              display:'flex', alignItems:'center', gap:10 },
  badge:   { background:'#00ff8820', color:'#00ff88', border:'1px solid #00ff8840',
             borderRadius:20, fontSize:11, padding:'2px 10px', fontFamily:"'Space Mono',monospace" },
  form:    { display:'flex', flexDirection:'column', gap:14 },
  field:   { display:'flex', flexDirection:'column', gap:6 },
  btn:     { background:'#00ff88', color:'#080b10', padding:'11px 22px', fontWeight:700,
             fontSize:14, alignSelf:'flex-start', borderRadius:8 },
  walletRow:{ display:'flex', alignItems:'center', justifyContent:'space-between',
              padding:'14px 0', borderBottom:'1px solid', gap:12 },
  walletMain:{ display:'flex', flexDirection:'column', gap:4, flex:1, minWidth:0 },
  walletAddr:{ display:'flex', alignItems:'center', gap:8 },
  walletMeta:{ fontSize:12, color:'#7a94b0', fontFamily:"'Space Mono',monospace" },
  walletActions:{ display:'flex', gap:8, flexShrink:0 },
  btnSm:   { background:'#131920', color:'#e0eaf5', border:'1px solid #1e2d3d',
             padding:'6px 14px', fontSize:12, borderRadius:6 },
  btnDanger:{ background:'#ff4d6a15', color:'#ff4d6a', borderColor:'#ff4d6a30' },
  dot:     (active) => ({ width:8, height:8, borderRadius:'50%', flexShrink:0,
              background: active ? '#00ff88' : '#7a94b0',
              boxShadow: active ? '0 0 6px #00ff88' : 'none' }),
  lbl:     { background:'#4d9eff15', color:'#4d9eff', border:'1px solid #4d9eff30',
             borderRadius:4, fontSize:11, padding:'2px 8px' },
  mono:    { fontFamily:"'Space Mono',monospace", fontSize:12, color:'#e0eaf5' },
  dim:     { color:'#7a94b0', fontStyle:'italic', padding:'8px 0' },
  actRow:  { display:'flex', alignItems:'center', gap:12, padding:'10px 0',
             borderBottom:'1px solid #1e2d3d', flexWrap:'wrap' },
  tag:     { fontSize:10, fontWeight:700, letterSpacing:'0.06em', textTransform:'uppercase',
             padding:'3px 8px', borderRadius:4, border:'1px solid', flexShrink:0 },
  actDetail:{ flex:1, fontSize:13, color:'#e0eaf5' },
  txLink:  { fontSize:11, color:'#7a94b0', fontFamily:"'Space Mono',monospace" },
  time:    { fontSize:11, color:'#7a94b0', fontFamily:"'Space Mono',monospace", flexShrink:0 },
};
